import type { MouseEvent } from "react";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  Button,
  ButtonSize,
  ButtonVariant
} from "shared/components";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { useLiveViewContext } from "./LiveViewContext";
import { firstNameOf } from "./stageNotice";
import { LiveDot } from "./LiveDot";

// On mobile the applicant cell sits inside a link; keep clicks here.
const keepClick = (event: MouseEvent) => {
  event.preventDefault();
  event.stopPropagation();
};

/**
 * The applicant's subtitle line ("Self-serve application"), which becomes
 * "In application now · Income" plus "View live" while BoomView is on for
 * this partner. Invites and manual applications can't be watched.
 */
export const ApplicantLiveViewStatus = ({
  applicantId,
  applicantName,
  isInvite,
  isManual
}: {
  applicantId: number;
  applicantName: string;
  isInvite: boolean;
  isManual: boolean;
}) => {
  const liveView = useLiveViewContext();
  const watchable = Boolean(liveView) && !isInvite && !isManual;
  const entry = watchable ? liveView?.presence.get(applicantId) : undefined;

  return (
    <div className="mt-1 flex max-w-[240px] items-center justify-between gap-2">
      {entry ? (
        <Badge
          variant={BadgeVariant.SUCCESS}
          size={BadgeSize.SM}
          className="min-w-0 items-center"
          leftElement={<LiveDot className="mr-0.5" />}
        >
          <span className="truncate">In application now · {entry.step}</span>
        </Badge>
      ) : (
        <p className="min-w-0 truncate text-xs font-normal text-content-400">
          {isManual ? "Manual application" : "Self-serve application"}
        </p>
      )}

      {liveView && watchable ? (
        <Tooltip placement="top">
          <TooltipTrigger asChild>
            <span
              className="shrink-0"
              tabIndex={entry ? undefined : 0}
              onClick={keepClick}
            >
              <Button
                variant={ButtonVariant.TEXT}
                size={ButtonSize.SM}
                disabled={!entry}
                className={entry ? "" : "pointer-events-none"}
                onClick={() =>
                  liveView.watch({ id: applicantId, name: applicantName })
                }
              >
                View live
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent className="z-[60] max-w-[240px] rounded-lg p-3 text-sm text-content-1000-inverted">
            {entry
              ? `Watch ${firstNameOf(applicantName)}'s screen live`
              : `Available while ${firstNameOf(applicantName)} is in the application`}
          </TooltipContent>
        </Tooltip>
      ) : null}
    </div>
  );
};
